import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { api, fmtErr } from "../../lib/api";
import { limitToast } from "../../lib/limits";
import { PageHeader } from "./Dashboard";
import TourReplayButton from "../../components/tour/TourReplayButton";
import { Field, EmptyYacht } from "./Inventory";
import { Plus, Receipt, Pencil, Trash2, Copy, FileText, Star, Store } from "lucide-react";
import { ReviewDialog } from "../../components/SupplierReview";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import FileUploader, { AuthFileLink, PdfPreview } from "../../components/FileUploader";
import { VatBreakdown, ShowTaskButton } from "./maintenance/components";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { useRights } from "../../lib/useRights";
import { InvoiceMessages, InvoiceHistoryList } from "../../components/InvoiceThread";
import { InvoiceReviewBlock } from "../../components/SupplierReview";
import {
  SUPPLIER_CATEGORY_LABELS,
  SUPPLIER_CATEGORY_KEYS,
  labelForSupplierCategory,
} from "../../lib/supplierCategories";

// Invoice categories are synchronised 1:1 with the Network → Suppliers
// taxonomy. Single source of truth lives in lib/supplierCategories.js so
// the two dropdowns can never drift.
const CATEGORIES = SUPPLIER_CATEGORY_KEYS.map((k) => ({ value: k, label: SUPPLIER_CATEGORY_LABELS[k] }));

const DEFAULT_CATEGORY = "Engines & Propulsion";

const DEFAULT = {
  supplier_name: "", invoice_number: "", issue_date: "", due_date: "",
  amount: "", currency: "AED", category: DEFAULT_CATEGORY, status: "waiting",
  file_path: "", paid_date: "", notes: "",
  // `system` is kept for legacy rows (auto-created from a maintenance job
  // carry the system tag) — no longer collected from the create form.
  system: "", supplier_id: "",
  // Bank-of-record on this invoice. Pre-filled by the "Use supplier's bank
  // details" button on the editor (one-tap copy from the picked supplier's
  // address-book entry). All editable so an ad-hoc one-off payment to a
  // different account doesn't require touching the supplier card.
  bank_name: "", account_holder: "", iban: "", bic_swift: "",
  // Inline "add a new supplier" workflow — when the user types a name not
  // in the picker, we ask whether to save it to their network. These two
  // fields stay in the form-state but are stripped before POST /invoices
  // and replayed against POST /suppliers + /suppliers/{id}/assign.
  _new_supplier: false,
  _new_supplier_save: null,           // null | true | false
  _new_supplier_type: "company",
  _new_supplier_contact_person: "",
  _new_supplier_email: "",
  _new_supplier_phone: "",
};

// Status → pill colour. Mirrors the supplier-driven lifecycle:
// Task Confirmed → Awaiting payment → Payment done → Payment confirmed.
const STATUS_TONE = {
  task_confirmed:    { label: "Task Confirmed",    cls: "bg-sky-50 text-sky-700", supplierOnly: true },
  waiting:           { label: "Awaiting payment",  cls: "bg-amber-50 text-amber-700" },
  dispute:           { label: "In dispute",        cls: "bg-red-50 text-red-700" },
  paid:              { label: "Payment done",      cls: "bg-emerald-50 text-emerald-700" },
  payment_confirmed: { label: "Payment confirmed", cls: "bg-emerald-600 text-white", supplierOnly: true },
};

function StatusPill({ value }) {
  const tone = STATUS_TONE[value] || STATUS_TONE.waiting;
  // Badge look identical to the supplier's Invoices page.
  return (
    <span className={`inline-flex items-center text-xs px-2.5 py-0.5 rounded-full font-medium whitespace-nowrap ${tone.cls}`}>
      {tone.label}
    </span>
  );
}

/** True when the supplier card carries any pay-to info worth copying. */
function supplierHasBank(s) {
  if (!s) return false;
  return Boolean(s.iban || s.bic_swift || s.bank_name || s.account_holder);
}

export default function Invoices() {
  const { t } = useTranslation();
  const { activeYacht } = useYacht();
  const { can } = useRights();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(DEFAULT);
  // True while the "Unlock invoice" PATCH is in flight (status: paid →
  // waiting). Disables the button to prevent double-clicks while we
  // wait for the server to ack the carve-out, then we drop the lock UI
  // by mutating `editing.status` locally.
  const [unlocking, setUnlocking] = useState(false);
  // Read-only detail view that the card/row opens on click.
  const [detail, setDetail] = useState(null);
  // Suppliers list is loaded once per yacht — used for the picker on
  // "service_provider" category. Stays cheap because the suppliers
  // endpoint is already projected and indexed.
  const [suppliers, setSuppliers] = useState([]);
  // ?status=outstanding from the dashboard "Outstanding invoices" card
  // hides paid + cancelled rows. Clearable via the chip in the toolbar.
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("status") === "outstanding") {
      setOutstandingOnly(true);
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!activeYacht?.id) return;
    try {
      const [{ data: rows }, { data: s }, { data: supY }, { data: supN }] = await Promise.all([
        api.get(`/invoices?yacht_id=${activeYacht.id}`),
        api.get(`/invoices/summary/${activeYacht.id}`),
        api.get(`/suppliers?yacht_id=${activeYacht.id}`).catch(() => ({ data: [] })),
        // Network-wide list — gated to seniors; crew gets 403 which we swallow.
        api.get(`/suppliers`).catch(() => ({ data: [] })),
      ]);
      setItems(rows);
      setSummary(s);
      // ?open={invoiceId} deep-link (from a task's "Show invoice" button) —
      // pop the read-only detail dialog for that invoice, then clean the URL.
      const openId = searchParams.get("open");
      if (openId) {
        const row = rows.find((r) => r.id === openId);
        if (row) setDetail(row);
        const next = new URLSearchParams(searchParams);
        next.delete("open");
        setSearchParams(next, { replace: true });
      }
      // Merge both sources, dedupe by id, prefer yacht-scoped row (richer
      // assignment info) when both return the same supplier.
      const map = new Map();
      [...(supY || []), ...(supN || [])].forEach((sup) => {
        if (sup && sup.id && !map.has(sup.id)) map.set(sup.id, sup);
      });
      setSuppliers([...map.values()]);
    } catch {
      setItems([]); setSummary(null); setSuppliers([]);
    }
  };
  useEffect(() => {
    setItems([]); setSummary(null); setDetail(null); load();
    // eslint-disable-next-line
  }, [activeYacht?.id]);

  // Strip the form-local _new_supplier_* fields before POST, optionally
  // creating the supplier in the user's network first and assigning it
  // to the active yacht. Returns the payload-shaped form.
  const materializeSupplier = async (f) => {
    let supplierId = f.supplier_id || "";
    let supplierName = (f.supplier_name || "").trim();
    if (f._new_supplier && f._new_supplier_save === true) {
      // Create + assign. Failure here is fatal for the invoice POST.
      const body = {
        type: f._new_supplier_type || "company",
        name: supplierName,
        contact_person: f._new_supplier_contact_person || "",
        email: (f._new_supplier_email || "").trim(),
        phone: (f._new_supplier_phone || "").trim(),
        category: f.category || DEFAULT_CATEGORY,
        notes: "",
      };
      const { data: sup } = await api.post("/suppliers", body);
      // Best-effort yacht assignment so the picker shows it next time.
      try {
        await api.post(`/suppliers/${sup.id}/assign`, { yacht_id: activeYacht.id });
      } catch { /* non-fatal */ }
      supplierId = sup.id;
      supplierName = sup.name;
    }
    const out = { ...f, supplier_id: supplierId, supplier_name: supplierName };
    // Drop UI-only fields before sending to /api/invoices.
    delete out._new_supplier;
    delete out._new_supplier_save;
    delete out._new_supplier_type;
    delete out._new_supplier_contact_person;
    delete out._new_supplier_email;
    delete out._new_supplier_phone;
    return out;
  };

  const save = async () => {
    try {
      const clean = await materializeSupplier(form);
      const payload = {
        ...clean, yacht_id: activeYacht.id,
        amount: Number(clean.amount || 0),
      };
      await api.post("/invoices", payload);
      toast.success("Invoice added");
      setForm(DEFAULT); setOpen(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const clean = await materializeSupplier(editForm);
      const payload = {
        ...clean, yacht_id: activeYacht.id,
        amount: Number(clean.amount || 0),
      };
      await api.patch(`/invoices/${editing.id}`, payload);
      toast.success("Invoice updated");
      setEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const unlockPaidInvoice = async () => {
    // No browser confirm — the button copy is explicit, and the user
    // can always re-mark the invoice as paid from the same Edit dialog
    // after they make their changes.
    if (!editing) return;
    setUnlocking(true);
    try {
      // The backend's 2026-06-11 carve-out accepts a status flip away
      // from `paid` while the rest of the invoice fields are unchanged.
      // We mirror the current form so a captain's in-progress edits
      // aren't dropped if they typed before clicking Unlock.
      const clean = await materializeSupplier(editForm);
      const payload = {
        ...clean,
        yacht_id: activeYacht.id,
        amount: Number(clean.amount || 0),
        status: "waiting",
        // Drop paid_date when re-opening — it'll be set again on the
        // next paid transition by the same backend hook.
        paid_date: null,
      };
      const { data: fresh } = await api.patch(`/invoices/${editing.id}`, payload);
      setEditing({ ...editing, status: fresh.status, paid_date: fresh.paid_date });
      setEditForm((f) => ({ ...f, status: fresh.status, paid_date: "" }));
      toast.success("Invoice unlocked — make your changes and Save");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setUnlocking(false);
    }
  };


  const delFromEdit = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    try {
      await api.delete(`/invoices/${editing.id}`);
      toast.success("Invoice deleted");
      setEditing(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!activeYacht) return <EmptyYacht />;

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={activeYacht.name}
        title={t("invoices.title")}
        right={
          can("document:create") ? (
            <div className="flex items-center gap-2">
              <TourReplayButton tourId="invoices" />
              <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(DEFAULT); }}>
                <DialogTrigger asChild>
                  <Button className="bg-ink hover:bg-black" data-testid="add-invoice-btn">
                    <Plus className="h-4 w-4 mr-2" />{t("invoices.new_invoice")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{t("invoices.new_invoice")}</DialogTitle></DialogHeader>
                  <InvoiceFormFields
                    form={form} setForm={setForm}
                    yachtId={activeYacht.id} suppliers={suppliers}
                    mode="create"
                  />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={save} className="bg-ink hover:bg-black" disabled={!isFormValid(form)} data-testid="save-invoice">Add invoice</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          ) : null
        }
      />

      {summary && (
        <div className="mt-8 grid grid-cols-2 gap-2 sm:gap-3">
          <StatCard label="Total invoiced" value={`AED ${summary.total_amount.toFixed(2)}`} testid="inv-total" />
          <StatCard label="Outstanding" value={`AED ${summary.outstanding_amount.toFixed(2)}`} testid="inv-outstanding" tone={summary.outstanding_amount > 0 ? "amber" : "default"} />
          <StatCard label="Overdue" value={summary.overdue_count} testid="inv-overdue" tone={summary.overdue_count > 0 ? "red" : "default"} />
          <StatCard label="Total invoices" value={summary.total_count} testid="inv-count" />
        </div>
      )}

      {outstandingOnly && (
        <div className="mt-4 flex">
          <button
            type="button"
            onClick={() => setOutstandingOnly(false)}
            className="inline-flex items-center gap-1.5 h-9 rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-3 text-xs"
            data-testid="inv-outstanding-chip"
          >
            Waiting &amp; disputed only (hides paid)
            <span aria-hidden className="text-amber-500">×</span>
          </button>
        </div>
      )}

      {(() => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const SETTLED = new Set(["paid", "payment_confirmed", "cancelled"]);
        const isOverdue = (it) => !!it.due_date && it.due_date.slice(0, 10) < todayStr && !SETTLED.has(it.status);
        const base = outstandingOnly
          ? items.filter((it) => it.status === "waiting" || it.status === "dispute" || it.status === "task_confirmed")
          : items;
        // Overdue invoices pinned to the top (oldest due date first); the
        // rest keep their original newest-first order (stable sort).
        const visible = [...base].sort((a, b) => {
          const ao = isOverdue(a), bo = isOverdue(b);
          if (ao !== bo) return ao ? -1 : 1;
          if (ao && bo) return (a.due_date || "").localeCompare(b.due_date || "");
          return 0;
        });
        const overdueDays = (it) => Math.max(Math.floor((Date.parse(todayStr) - Date.parse(it.due_date.slice(0, 10))) / 86400000), 1);
        if (visible.length === 0) {
          return (
            <div className="mt-4 rounded-md border border-zinc-200 bg-white p-10 text-center text-zinc-500">
              <Receipt className="h-6 w-6 mx-auto text-zinc-300" />
              <div className="mt-2">
                {outstandingOnly ? "No waiting or disputed invoices." : t("invoices.no_invoices")}
              </div>
            </div>
          );
        }
        return (
          /* Supplier-style card list — same look & feel as the supplier's
             Invoices page (single layout for all screen sizes). */
          <div className="mt-4 space-y-2" data-testid="inv-cards">
            {visible.map((it) => {
              const CARD_TONE = {
                task_confirmed: ["border-sky-200", "bg-sky-50/70 border-sky-100"],
                waiting: ["border-amber-200", "bg-amber-50/70 border-amber-100"],
                dispute: ["border-red-200", "bg-red-50/70 border-red-100"],
                paid: ["border-emerald-200", "bg-emerald-50/70 border-emerald-100"],
                payment_confirmed: ["border-emerald-200", "bg-emerald-50/70 border-emerald-100"],
              };
              const [borderCls, headerCls] = isOverdue(it)
                ? ["border-red-300 ring-1 ring-red-200", "bg-red-50 border-red-100"]
                : CARD_TONE[it.status] || ["border-zinc-200", "bg-zinc-50 border-zinc-100"];
              return (
              <div
                key={it.id}
                className={`rounded-xl border overflow-hidden cursor-pointer transition-shadow hover:shadow-md ${borderCls}`}
                data-testid={`inv-row-${it.id}`}
                role="button"
                tabIndex={0}
                onClick={() => setDetail(it)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetail(it); } }}
              >
                <div className={`flex items-start gap-2 px-3 py-2 border-b ${headerCls}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-sky-700 truncate">
                      <Store className="h-3 w-3 shrink-0" />{it.supplier_name}
                    </div>
                    <div className="font-semibold text-sm flex items-center gap-1.5 text-zinc-900">
                      Invoice {it.invoice_number || "(no number)"}
                      {it.unread > 0 && <span className="rounded-full bg-red-500 text-white text-[10px] px-1.5 leading-4" data-testid={`inv-unread-${it.id}`}>{it.unread}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm font-semibold">{it.currency} {it.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div className="mt-0.5 flex items-center justify-end gap-1.5">
                      {isOverdue(it) && (
                        <span className="inline-flex rounded-full bg-red-600 text-white text-[10px] px-1.5 py-0.5 font-semibold uppercase tracking-wide" data-testid={`inv-overdue-badge-${it.id}`}>
                          Overdue {overdueDays(it)}d
                        </span>
                      )}
                      <StatusPill value={it.status} />
                    </div>
                  </div>
                </div>
                <div className="px-3 py-2 flex flex-wrap items-center gap-x-2 gap-y-1 bg-white">
                  {it.task_title && <span className="text-[11px] font-medium text-zinc-700 truncate max-w-full">{it.task_title}</span>}
                  <span className="flex flex-wrap items-center gap-x-2 text-[11px] text-zinc-500">
                    <span>issued {(it.issue_date || it.created_at || "").slice(0, 10)}</span>
                    {it.created_by_name && <span>· by {it.created_by_name}</span>}
                    <span>· {labelForCategory(it.category)}</span>
                    {it.system && <span>· {it.system}</span>}
                    {it.due_date && !SETTLED.has(it.status) && (
                      <span className={isOverdue(it) ? "text-red-600 font-medium" : ""}>· due {it.due_date.slice(0, 10)}</span>
                    )}
                    {it.status === "paid" && it.paid_date && <span>· paid {it.paid_date.slice(0, 10)}</span>}
                  </span>
                  {it.linked_maintenance_id && (
                    <span className="ml-auto">
                      <ShowTaskButton maintId={it.linked_maintenance_id} testid={`show-task-btn-${it.id}`} />
                    </span>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        );
      })()}

      {/* Edit / details all-in-one dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inv-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>Edit invoice</span>
              {editing && <StatusPill value={editing.status} />}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <>
              {editing.status === "paid" && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-start justify-between gap-3">
                  <div className="flex-1">
                    This invoice is marked <strong>Payment DONE</strong> and is locked for edits.
                    Unlock it below to re-open and edit.
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={unlockPaidInvoice}
                    disabled={unlocking}
                    data-testid="inv-unlock"
                    className="border-emerald-300 text-emerald-900 hover:bg-emerald-100 shrink-0"
                  >
                    {unlocking ? "Unlocking…" : "Unlock invoice"}
                  </Button>
                </div>
              )}
              <InvoiceFormFields
                form={editForm} setForm={setEditForm}
                yachtId={activeYacht.id} suppliers={suppliers}
                mode="edit" lockedReason={editing.status === "paid" ? "paid" : null}
              />
              <FilePreview entry={editForm} />
            </>
          )}
          <DialogFooter className="flex justify-between">
            <div>
              {can("document:delete") && editing && (
                <Button variant="ghost" onClick={delFromEdit} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="inv-edit-delete">
                  <Trash2 className="h-4 w-4 mr-2" />Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                onClick={saveEdit}
                className="bg-ink hover:bg-black"
                disabled={!isFormValid(editForm)}
                data-testid="save-inv-edit"
              >
                Save changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Read-only detail modal — opens when the user taps a row/card.
          History is shown inline; task-rendered invoices get the owner
          Payment done / Dispute actions instead of the Edit dialog. */}
      <InvoiceDetailDialog
        entry={detail}
        can={can}
        onClose={() => setDetail(null)}
        onMessagesUpdated={(fresh) => { setDetail((prev) => (prev ? { ...prev, ...fresh } : fresh)); load(); }}
        onEdit={() => {
          const it = detail;
          setDetail(null);
          setEditing(it);
          setEditForm({ ...DEFAULT, ...it });
        }}
      />
    </div>
  );
}

// Validation rule: supplier name + non-zero amount are always required.
// If the user opted to save the typed supplier to their network, email +
// phone are also mandatory (mirrors POST /suppliers schema).
function isFormValid(f) {
  if (!f.supplier_name || !f.amount) return false;
  if (f._new_supplier && f._new_supplier_save === null) return false;
  if (f._new_supplier && f._new_supplier_save === true) {
    if (!(f._new_supplier_email || "").trim()) return false;
    if (!(f._new_supplier_phone || "").trim()) return false;
  }
  return true;
}

function labelForCategory(value) {
  return labelForSupplierCategory(value);
}

function StatCard({ label, value, testid, tone = "default" }) {
  const toneCls = tone === "amber"
    ? "border-amber-200 bg-amber-50"
    : tone === "red"
    ? "border-red-200 bg-red-50"
    : "border-zinc-200 bg-white";
  return (
    <div className={`rounded-md border p-3 sm:px-4 sm:py-3.5 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="mt-1 font-display text-lg sm:text-2xl font-medium leading-tight break-words">{value}</div>
    </div>
  );
}

function InvoiceFormFields({ form, setForm, yachtId, suppliers, mode, lockedReason }) {
  // Split suppliers into those matching the picked category vs the rest.
  // Legacy rows can carry pre-unified category names, so both sides of the
  // comparison are normalised through the taxonomy map — and any supplier
  // that still doesn't match stays reachable under "Other categories" so the
  // full network is always pickable. Deduped by id (yacht + network merge).
  const { matchingSuppliers, otherSuppliers } = useMemo(() => {
    const seen = new Map();
    (suppliers || []).forEach((s) => {
      if (s && s.id && !seen.has(s.id)) seen.set(s.id, s);
    });
    const matching = [], others = [];
    [...seen.values()].forEach((s) => {
      const cat = s.category ? labelForSupplierCategory(s.category) : "";
      if (!form.category || !cat || cat === labelForSupplierCategory(form.category)) matching.push(s);
      else others.push(s);
    });
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
    return { matchingSuppliers: matching.sort(byName), otherSuppliers: others.sort(byName) };
  }, [suppliers, form.category]);
  const supplierOptions = useMemo(
    () => [...matchingSuppliers, ...otherSuppliers],
    [matchingSuppliers, otherSuppliers],
  );

  // Picker change handler — supports three special tokens:
  //   ""        → no selection yet (initial state)
  //   "__new__" → user wants to add a brand-new supplier inline
  //   <id>      → existing supplier picked, mirror name into supplier_name
  const onPickerChange = (val) => {
    if (val === "__new__") {
      setForm({
        ...form,
        supplier_id: "",
        supplier_name: "",
        _new_supplier: true,
        _new_supplier_save: null,
        _new_supplier_type: "company",
        _new_supplier_contact_person: "",
        _new_supplier_email: "",
        _new_supplier_phone: "",
      });
      return;
    }
    const sup = supplierOptions.find((s) => s.id === val);
    setForm({
      ...form,
      supplier_id: val,
      supplier_name: sup ? sup.name : "",
      _new_supplier: false,
      _new_supplier_save: null,
    });
  };

  // Which option is currently selected in the picker?
  const pickerValue = form._new_supplier ? "__new__" : (form.supplier_id || "");

  // Full supplier object for the currently picked id (if any). Drives the
  // "Use supplier's bank details" affordance — visible only when we have
  // a registered supplier AND that supplier carries bank info on its
  // address-book entry.
  const pickedSupplier = useMemo(
    () => (form.supplier_id ? supplierOptions.find((s) => s.id === form.supplier_id) : null),
    [supplierOptions, form.supplier_id],
  );

  const fillBankFromSupplier = (sup) => {
    if (!sup) return;
    setForm({
      ...form,
      bank_name: sup.bank_name || "",
      account_holder: sup.account_holder || "",
      iban: sup.iban || "",
      bic_swift: sup.bic_swift || "",
    });
    toast.success(`Bank details copied from ${sup.name}`);
  };

  // In EDIT mode the invoice is already tied to a supplier + category, so
  // those are read-only. Bank details are locked when they come from the
  // supplier's own profile; otherwise the crew may fill them in.
  const isEdit = mode === "edit";
  const bankLocked = isEdit && !!form.bank_from_supplier;

  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label={isEdit ? "Category" : "Category *"} className="col-span-2">
        {isEdit ? (
          <div className="h-9 flex items-center px-3 text-sm text-zinc-700 rounded-md border border-zinc-200 bg-zinc-50" data-testid="inv-category-readonly">
            {labelForCategory(form.category)}
          </div>
        ) : (
          <select
            className="h-9 w-full rounded-md border border-input px-3 text-sm"
            value={form.category || ""}
            onChange={(e) => setForm({
              ...form,
              category: e.target.value,
              // Reset supplier selection when category changes — the picker
              // is category-scoped so the previously selected supplier may
              // no longer be valid.
              supplier_id: "",
              supplier_name: "",
              _new_supplier: false,
              _new_supplier_save: null,
            })}
            data-testid="inv-category"
          >
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        )}
      </Field>

      <Field label={isEdit ? "Vendor / Supplier" : "Vendor / Supplier *"} className="col-span-2">
        {isEdit ? (
          <div className="h-9 flex items-center px-3 text-sm text-zinc-700 rounded-md border border-zinc-200 bg-zinc-50" data-testid="inv-supplier-readonly">
            {form.supplier_name || "—"}
          </div>
        ) : (
          <>
            <select
              className="h-9 w-full rounded-md border border-input px-3 text-sm"
              value={pickerValue}
              onChange={(e) => onPickerChange(e.target.value)}
              data-testid="inv-supplier-picker"
            >
              <option value="">— pick a supplier in this category —</option>
              {matchingSuppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
              {otherSuppliers.length > 0 && (
                <optgroup label="Other categories">
                  {otherSuppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} — {labelForSupplierCategory(s.category)}
                    </option>
                  ))}
                </optgroup>
              )}
              <option value="__new__">+ Add new supplier</option>
            </select>
            {matchingSuppliers.length === 0 && otherSuppliers.length === 0 && pickerValue === "" && (
              <div className="text-[11px] text-zinc-500 mt-1">
                No suppliers in this category yet. Pick <em>+ Add new supplier</em> to create one.
              </div>
            )}
          </>
        )}
      </Field>

      {form._new_supplier && (
        <div className="col-span-2 rounded-md border border-zinc-200 bg-zinc-50 p-3 space-y-3" data-testid="inv-new-supplier-box">
          <Field label="Supplier name *">
            <Input
              value={form.supplier_name}
              onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
              data-testid="inv-supplier-new-name"
              placeholder="e.g. Med Marine Services"
            />
          </Field>
          <div>
            <div className="text-xs font-medium text-zinc-700 mb-1.5">
              Add this supplier to your network?
            </div>
            <div className="flex gap-2" data-testid="inv-new-supplier-save-choice">
              <button
                type="button"
                onClick={() => setForm({ ...form, _new_supplier_save: true })}
                className={`text-xs rounded-md px-3 py-1.5 border ${
                  form._new_supplier_save === true
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid="inv-new-supplier-yes"
              >
                Yes — save to network
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, _new_supplier_save: false })}
                className={`text-xs rounded-md px-3 py-1.5 border ${
                  form._new_supplier_save === false
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid="inv-new-supplier-no"
              >
                No — just this invoice
              </button>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1.5">
              Saving the supplier lets you reuse them across yachts and reach them from the Network page.
            </div>
          </div>
          {form._new_supplier_save === true && (
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-200">
              <Field label="Type">
                <select
                  className="h-9 w-full rounded-md border border-input px-3 text-sm"
                  value={form._new_supplier_type}
                  onChange={(e) => setForm({ ...form, _new_supplier_type: e.target.value })}
                  data-testid="inv-new-supplier-type"
                >
                  <option value="company">Company</option>
                  <option value="individual">Individual</option>
                </select>
              </Field>
              <Field label="Contact person">
                <Input
                  value={form._new_supplier_contact_person}
                  onChange={(e) => setForm({ ...form, _new_supplier_contact_person: e.target.value })}
                  data-testid="inv-new-supplier-contact"
                />
              </Field>
              <Field label="Email *">
                <Input
                  type="email"
                  value={form._new_supplier_email}
                  onChange={(e) => setForm({ ...form, _new_supplier_email: e.target.value })}
                  data-testid="inv-new-supplier-email"
                />
              </Field>
              <Field label="Phone *">
                <Input
                  value={form._new_supplier_phone}
                  onChange={(e) => setForm({ ...form, _new_supplier_phone: e.target.value })}
                  data-testid="inv-new-supplier-phone"
                />
              </Field>
            </div>
          )}
        </div>
      )}

      <Field label="Invoice #">
        <Input value={form.invoice_number || ""} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="inv-number" />
      </Field>
      <Field label="Amount *">
        <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="inv-amount" />
      </Field>
      <Field label="Currency">
        <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
      </Field>
      <Field label="Status">
        <select
          className="h-9 w-full rounded-md border border-input px-3 text-sm"
          value={form.status || "waiting"}
          onChange={(e) => setForm({ ...form, status: e.target.value })}
          data-testid="inv-status"
        >
          {Object.entries(STATUS_TONE).map(([k, v]) => (
            // Supplier-owned statuses are visible (correct current value) but
            // not selectable — only the supplier moves the invoice into them.
            <option key={k} value={k} disabled={v.supplierOnly}>{v.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Issue date">
        <Input type="date" value={form.issue_date || ""} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
      </Field>
      <Field label="Due date">
        <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
      </Field>
      {form.status === "paid" && (
        <Field label="Paid date" className="col-span-2">
          <Input type="date" value={form.paid_date || ""} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} />
        </Field>
      )}

      {/* Bank-of-record on this invoice. Pre-filled by the "Use supplier's
          bank details" button when a registered supplier is picked and
          their card has bank info. Always editable so a one-off payment
          to a different account doesn't require touching the supplier
          card. */}
      <div className="col-span-2 rounded-md border border-zinc-200 bg-zinc-50/40 p-3 space-y-3" data-testid="inv-bank-block">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-600">
            Pay to (bank details)
          </div>
          {bankLocked ? (
            <span className="text-[11px] text-zinc-500" data-testid="inv-bank-from-supplier">From supplier's profile</span>
          ) : (pickedSupplier && supplierHasBank(pickedSupplier) && (
            <button
              type="button"
              onClick={() => fillBankFromSupplier(pickedSupplier)}
              className="text-xs text-ink underline underline-offset-2 hover:text-black"
              data-testid="inv-prefill-bank"
              title={`Copy IBAN / BIC / bank name from ${pickedSupplier.name}`}
            >
              Use {pickedSupplier.name}&apos;s bank details
            </button>
          ))}
        </div>
        {bankLocked ? (
          <dl className="grid grid-cols-3 gap-x-3 gap-y-1 text-sm" data-testid="inv-bank-readonly">
            {form.bank_name && (<><dt className="text-zinc-500">Bank</dt><dd className="col-span-2">{form.bank_name}</dd></>)}
            {form.account_holder && (<><dt className="text-zinc-500">Holder</dt><dd className="col-span-2">{form.account_holder}</dd></>)}
            {form.iban && (<><dt className="text-zinc-500">IBAN</dt><dd className="col-span-2 font-mono text-xs break-all">{form.iban}</dd></>)}
            {form.account_number && (<><dt className="text-zinc-500">Account</dt><dd className="col-span-2 font-mono text-xs break-all">{form.account_number}</dd></>)}
            {form.bic_swift && (<><dt className="text-zinc-500">BIC</dt><dd className="col-span-2 font-mono text-xs">{form.bic_swift}</dd></>)}
          </dl>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Bank name">
              <Input
                value={form.bank_name || ""}
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                data-testid="inv-bank-name"
                placeholder="optional"
              />
            </Field>
            <Field label="Account holder">
              <Input
                value={form.account_holder || ""}
                onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
                data-testid="inv-account-holder"
                placeholder="optional"
              />
            </Field>
            <Field label="IBAN">
              <Input
                value={form.iban || ""}
                onChange={(e) => setForm({ ...form, iban: e.target.value })}
                data-testid="inv-iban"
                placeholder="optional"
              />
            </Field>
            <Field label="BIC / SWIFT">
              <Input
                value={form.bic_swift || ""}
                onChange={(e) => setForm({ ...form, bic_swift: e.target.value })}
                data-testid="inv-bic-swift"
                placeholder="optional"
              />
            </Field>
          </div>
        )}
      </div>

      <Field label="Notes" className="col-span-2">
        <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <div className="col-span-2 flex items-end">
        <FileUploader
          yacht_id={yachtId}
          folder="invoices"
          accept="image/*,application/pdf"
          onUploaded={(path) => setForm({ ...form, file_path: path })}
          label={form.file_path ? "Invoice file attached" : "Attach invoice file (PDF or image)"}
          testid="inv-file"
        />
      </div>
    </div>
  );
}

// Renders the previously uploaded file inside the Edit dialog so users
// can verify what's attached without opening a separate detail dialog.
function FilePreview({ entry }) {
  if (!entry?.file_path) return null;
  const isPdf = /\.pdf$/i.test(entry.file_path);
  const isImage = /\.(png|jpe?g|webp|heic|heif|gif)$/i.test(entry.file_path);
  return (
    <div className="mt-2 border-t border-zinc-200 pt-4">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Attached file</div>
      {isPdf ? (
        <div className="space-y-2">
          <div className="max-h-72 overflow-y-auto rounded-md">
            <PdfPreview path={entry.file_path} width={360} testid="inv-pdf" />
          </div>
          <AuthFileLink path={entry.file_path} className="text-xs" testid="inv-open-file">
            Open full PDF →
          </AuthFileLink>
        </div>
      ) : isImage ? (
        <AuthFileLink path={entry.file_path} testid="inv-open-file">
          {entry.file_path.split("/").pop()}
        </AuthFileLink>
      ) : (
        <AuthFileLink path={entry.file_path} testid="inv-open-file">
          {entry.file_path.split("/").pop()}
        </AuthFileLink>
      )}
    </div>
  );
}

/* ─── Invoice detail modal ─────────────────────────────────────────────
 * Read-only summary opened by tapping a card/row. Two footer actions:
 * Edit (jumps to the existing edit dialog) and History (opens the
 * existing status-history dialog). Same UX pattern as Tasks / Periodic
 * tasks / Checklists / Logbook.
 */
function InvoiceDetailDialog({ entry, can, onClose, onEdit, onMessagesUpdated }) {
  const entryId = entry?.id;
  const status = entry?.status;
  const [pendingReview, setPendingReview] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  useEffect(() => {
    if (!entryId || status !== "payment_confirmed") { setPendingReview(null); return; }
    api.get("/reviews/pending")
      .then(({ data }) => setPendingReview((data || []).find((p) => p.invoice_id === entryId) || null))
      .catch(() => setPendingReview(null));
  }, [entryId, status]);
  if (!entry) return null;
  const fromTask = !!entry.linked_maintenance_id;
  const offline = fromTask && !!entry.track_offline;
  const confirmedAt = entry.task_confirmed_at || (fromTask ? entry.created_at : null);
  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 p-4 sm:p-6"
        data-testid="inv-detail-dialog"
      >
        {/* ── Block 1: supplier · invoice#+status · task+category · confirmed · description ── */}
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg break-words pr-6">{entry.supplier_name}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex items-center gap-2 flex-wrap" data-testid="inv-detail-number-row">
              <span className="text-sm text-zinc-700">Invoice {entry.invoice_number || "(no number)"}</span>
              <StatusPill value={entry.status} />
              {offline && (
                <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide bg-zinc-200 text-zinc-700" data-testid="inv-detail-offline-badge">
                  Tracked offline
                </span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {fromTask && (
          <div className="-mt-1">
            <ShowTaskButton maintId={entry.linked_maintenance_id} testid="inv-detail-show-task" />
          </div>
        )}

        {pendingReview && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-center justify-between gap-3" data-testid="inv-rate-banner">
            <span className="text-sm text-amber-800">How was this supplier's work?</span>
            <Button
              size="sm" variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-100 shrink-0 bg-white"
              onClick={() => setReviewOpen(true)}
              data-testid="inv-rate-supplier"
            >
              <Star className="h-3.5 w-3.5 mr-1.5" />Rate supplier
            </Button>
          </div>
        )}
        <ReviewDialog
          pending={reviewOpen ? pendingReview : null}
          onClose={() => setReviewOpen(false)}
          onDone={() => { setReviewOpen(false); setPendingReview(null); }}
        />

        {entry.pending_deletion && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900" data-testid="inv-pending-deletion">
            <span className="font-medium">Deletion requested</span> by {entry.pending_deletion.requested_by_name || "the captain"}.
            {can("yacht:edit") ? (
              <span className="ml-2 inline-flex gap-3">
                <button type="button" className="underline font-semibold" data-testid="inv-deletion-approve"
                  onClick={async () => {
                    try {
                      await api.post(`/deletion-requests/${entry.pending_deletion.id}/approve`);
                      toast.success("Deletion approved — invoice deleted");
                      onClose?.(); onMessagesUpdated?.();
                    } catch (e) { toast.error(fmtErr(e)); }
                  }}>Approve</button>
                <button type="button" className="underline" data-testid="inv-deletion-reject"
                  onClick={async () => {
                    try {
                      await api.post(`/deletion-requests/${entry.pending_deletion.id}/reject`);
                      toast.success("Deletion rejected");
                      onMessagesUpdated?.();
                    } catch (e) { toast.error(fmtErr(e)); }
                  }}>Reject</button>
              </span>
            ) : (
              <span className="ml-1 text-red-700">Awaiting owner/manager approval.</span>
            )}
          </div>
        )}

        <div data-testid="inv-detail-task">
          {entry.task_title && <div className="text-sm font-medium text-zinc-900">{entry.task_title}</div>}
          <div className="text-xs text-zinc-500 mt-0.5">
            {labelForCategory(entry.category)}
            {entry.system && <> · {entry.system}</>}
          </div>
        </div>
        {confirmedAt && (
          <div className="text-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Task confirmed</div>
            <div data-testid="inv-detail-confirmed-at">{confirmedAt.slice(0, 10)}</div>
          </div>
        )}
        {entry.task_description && (
          <div data-testid="inv-detail-description">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Description</div>
            <div className="text-sm text-zinc-700 whitespace-pre-wrap">{entry.task_description}</div>
          </div>
        )}

        {/* ── Block 2: scope · amount · number/issue/due · bank · pdf · actions ── */}
        {(entry.line_items || []).length > 0 && (
          <div className="mt-3 border-t border-zinc-200 pt-3" data-testid="inv-detail-scope">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Scope</div>
            <ul className="space-y-0.5">
              {entry.line_items.map((s, i) => (
                <li key={i} className="flex items-start justify-between gap-3 text-sm text-zinc-800">
                  <span className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]"><span className="text-zinc-400 font-medium mr-1">{i + 1}.</span>{s.text}</span>
                  {s.price != null && <span className="text-zinc-500 text-xs whitespace-nowrap shrink-0">{entry.currency} {Number(s.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                </li>
              ))}
            </ul>
            <VatBreakdown items={entry.line_items} amount={entry.amount} currency={entry.currency} vatPercent={entry.vat_percent} testid="inv-detail-vat" />
            {entry.vat_percent != null && (
              <div className="mt-1 pt-1 border-t border-zinc-200 flex items-baseline justify-between text-xs">
                <span className="text-zinc-500">Total incl. VAT</span>
                <span className="font-medium text-zinc-800">{entry.currency} {Number(entry.amount).toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {entry.linked_maintenance_id && <InvoiceTaskDocs taskId={entry.linked_maintenance_id} />}

        {/* Proof of payment — visible in all statuses once attached */}
        {entry.payment_confirmation_path && !["waiting", "paid"].includes(entry.status) && (
          <div className="mt-2">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${entry.payment_confirmation_path}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-emerald-700 border border-emerald-200 bg-emerald-50 rounded px-2 py-1 hover:bg-emerald-100"
              data-testid="inv-detail-payment-confirmation"
            >
              <FileText className="h-3 w-3" /> Payment confirmation
            </a>
          </div>
        )}

        <div className={`grid grid-cols-2 gap-x-4 gap-y-3 text-sm ${(entry.line_items || []).length ? "mt-2" : "mt-3 border-t border-zinc-200 pt-3"}`}>
          <Field2 label="Amount">
            <span className="font-mono font-medium" data-testid="inv-detail-amount">{entry.currency} {Number(entry.amount).toFixed(2)}</span>
          </Field2>
          {entry.invoice_number && (
            <Field2 label="Invoice #">
              <span className="font-mono text-xs">{entry.invoice_number}</span>
            </Field2>
          )}
          <Field2 label="Issue date">
            {(entry.issue_date || "").slice(0, 10) || (fromTask ? (entry.track_offline ? "not set yet" : "not sent yet") : (entry.created_at || "").slice(0, 10) || "—")}
          </Field2>
          {entry.due_date && (
            <Field2 label="Due date">{entry.due_date.slice(0, 10)}</Field2>
          )}
          {entry.payment_date && (
            <Field2 label="Paid on">{entry.payment_date.slice(0, 10)}</Field2>
          )}
          {entry.created_by_name && (
            <Field2 label="Filed by">{entry.created_by_name}</Field2>
          )}
        </div>

        {entry.prepay_deduction > 0 && (
          <div className="mt-2 rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900" data-testid="inv-detail-prepay">
            <div className="flex items-baseline justify-between">
              <span>Prepayment received</span>
              <span className="font-mono">− {entry.currency} {Number(entry.prepay_deduction).toFixed(2)}</span>
            </div>
            <div className="flex items-baseline justify-between font-semibold mt-0.5 pt-0.5 border-t border-teal-200">
              <span>Balance due</span>
              <span className="font-mono" data-testid="inv-detail-balance-due">{entry.currency} {(Number(entry.amount) - Number(entry.prepay_deduction)).toFixed(2)}</span>
            </div>
          </div>
        )}

        {entry.notes && (
          <div className="mt-3 border-t border-zinc-200 pt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Notes</div>
            <div className="text-sm text-zinc-800 whitespace-pre-wrap">{entry.notes}</div>
          </div>
        )}

        {(entry.iban || entry.bic_swift || entry.bank_name || entry.account_holder || entry.account_number) && (
          <div className="mt-3 border-t border-zinc-200 pt-3" data-testid="inv-detail-bank">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Pay to</div>
            <dl className="grid grid-cols-3 gap-x-3 gap-y-1 text-sm">
              {entry.bank_name && (<>
                <dt className="text-zinc-500">Bank</dt>
                <dd className="col-span-2">{entry.bank_name}</dd>
              </>)}
              {entry.account_holder && (<>
                <dt className="text-zinc-500">Holder</dt>
                <dd className="col-span-2">{entry.account_holder}</dd>
              </>)}
              {entry.iban && (<>
                <dt className="text-zinc-500">IBAN</dt>
                <dd className="col-span-2 font-mono text-xs break-all flex items-center gap-2">
                  <span className="break-all">{String(entry.iban).replace(/\s/g, "").replace(/(.{4})/g, "$1 ").trim()}</span>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard?.writeText(entry.iban).then(() => toast.success("IBAN copied")).catch(() => toast.error("Couldn't copy — please select and copy manually")); }}
                    className="shrink-0 inline-flex items-center gap-1 rounded border border-zinc-300 px-1.5 py-0.5 text-[10px] font-sans text-zinc-600 hover:bg-zinc-100"
                    data-testid="inv-copy-iban"
                    title="Copy IBAN"
                  >
                    <Copy className="h-3 w-3" />Copy
                  </button>
                </dd>
              </>)}
              {entry.account_number && (<>
                <dt className="text-zinc-500">Account</dt>
                <dd className="col-span-2 font-mono text-xs break-all">{entry.account_number}</dd>
              </>)}
              {entry.bic_swift && (<>
                <dt className="text-zinc-500">BIC</dt>
                <dd className="col-span-2 font-mono text-xs">{entry.bic_swift}</dd>
              </>)}
              {(() => {
                // Transfer reference = invoice number (what the supplier's
                // bookkeeping matches on), falling back to the invoice id.
                const ref = (entry.invoice_number || "").trim() || entry.id;
                return (<>
                  <dt className="text-zinc-500">Reference</dt>
                  <dd className="col-span-2 font-mono text-xs break-all flex items-center gap-2">
                    <span className="break-all" data-testid="inv-payment-reference">{ref}</span>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard?.writeText(ref).then(() => toast.success("Reference copied")).catch(() => toast.error("Couldn't copy — please select and copy manually")); }}
                      className="shrink-0 inline-flex items-center gap-1 rounded border border-zinc-300 px-1.5 py-0.5 text-[10px] font-sans text-zinc-600 hover:bg-zinc-100"
                      data-testid="inv-copy-reference"
                      title="Copy payment reference"
                    >
                      <Copy className="h-3 w-3" />Copy
                    </button>
                  </dd>
                  <dt />
                  <dd className="col-span-2 -mt-0.5 text-[11px] font-sans text-zinc-400">
                    Use this as the transfer reference so the supplier can match your payment instantly.
                  </dd>
                </>);
              })()}
            </dl>
          </div>
        )}

        <FilePreview entry={entry} />

        {/* Owner payment actions — task-rendered invoices only. Offline-tracked
            invoices are fully managed by the yacht (edit + lifecycle). */}
        {fromTask && can("document:create") && (
          offline
            ? <OfflineInvoiceActions entry={entry} onUpdated={onMessagesUpdated} onDeleted={() => { onMessagesUpdated?.(); onClose?.(); }} />
            : <OwnerInvoiceActions entry={entry} onUpdated={onMessagesUpdated} />
        )}

        {/* Internal-task invoices (no online supplier account): senior crew may
            delete — same routing as offline invoices (captain → approval). */}
        {fromTask && !offline && !entry.supplier_user_id && can("document:delete") && (
          <InternalInvoiceDelete entry={entry} onUpdated={onMessagesUpdated} onDeleted={() => { onMessagesUpdated?.(); onClose?.(); }} />
        )}

        {/* ── Block 3: messages (online suppliers only) ── */}
        {!offline && (
        <div className="mt-3 border-t border-zinc-200 pt-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Messages</div>
          <InvoiceMessages
            invoiceId={entry.id}
            initialMessages={entry.messages || []}
            mySide="crew"
            onUpdated={onMessagesUpdated}
          />
        </div>
        )}

        {/* ── Block 4: history ── */}
        <div className="mt-3 border-t border-zinc-200 pt-3" data-testid="inv-detail-history">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">History</div>
          <InvoiceHistoryList history={entry.status_history || []} />
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto" data-testid="inv-detail-close">
            Close
          </Button>
          {!fromTask && can("document:create") && (
            <Button
              onClick={onEdit}
              className="w-full sm:w-auto bg-ink hover:bg-black"
              data-testid="inv-detail-edit"
            >
              <Pencil className="h-4 w-4 mr-2" />Edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Delete button for internal-task invoices — same approval routing as offline
 * invoices: owner/manager delete immediately, captain requests approval. */
function InternalInvoiceDelete({ entry, onUpdated, onDeleted }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="mt-3 flex justify-end">
      <Button
        size="sm" variant="outline" disabled={busy}
        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
        onClick={async () => {
          if (!window.confirm("Permanently delete this invoice? The task itself stays. This cannot be undone.")) return;
          setBusy(true);
          try {
            const { data } = await api.delete(`/invoices/${entry.id}`);
            if (data?.mode === "approval_requested") { toast.success("Deletion request sent to the owner/manager for approval"); onUpdated?.(); }
            else if (data?.mode === "approval_pending") toast.info("A deletion request is already awaiting approval");
            else { toast.success("Invoice deleted"); onDeleted?.(); }
          } catch (e) { toast.error(fmtErr(e)); }
          finally { setBusy(false); }
        }}
        data-testid="inv-internal-delete"
      >
        <Trash2 className="h-4 w-4 mr-1" />Delete invoice
      </Button>
    </div>
  );
}

/* Owner-side lifecycle actions on a task-rendered invoice:
 *   Awaiting payment → "Payment done"  or  "Dispute" (reason required,
 *   posted to the invoice thread + supplier notified). */function OwnerInvoiceActions({ entry, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [reason, setReason] = useState("");
  const setStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${entry.id}/status`, { status, ...extra });
      toast.success(status === "paid"
        ? "Marked as Payment done — the supplier will confirm receipt"
        : "Invoice disputed — the supplier has been notified");
      setDisputeOpen(false); setReason("");
      onUpdated?.(data);
    } catch (e) {
      if (!(status === "paid" && limitToast(e, { yacht_id: entry.yacht_id, kind: "invoice", ref_id: entry.id, label: entry.supplier_name || entry.invoice_number || "", amount: entry.amount }))) {
        toast.error(fmtErr(e));
      }
    }
    finally { setBusy(false); }
  };

  if (entry.status === "task_confirmed") {
    return (
      <div className="mt-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800" data-testid="inv-owner-waiting-send">
        The supplier is preparing their invoice. You'll be able to pay or dispute it once they send it.
      </div>
    );
  }
  if (entry.status === "dispute") {
    return (
      <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="inv-owner-disputed">
        You disputed this invoice — the supplier will correct it and send an updated version.
      </div>
    );
  }
  if (entry.status === "paid") {
    return (
      <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="inv-owner-paid-note">
        Payment marked as done — waiting for the supplier to confirm the money arrived.
      </div>
    );
  }
  if (entry.status === "payment_confirmed") {
    return (
      <InvoiceReviewBlock
        invoiceId={entry.id}
        supplierName={entry.supplier_name}
        canReview
      />
    );
  }
  if (entry.status !== "waiting") return null;
  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="inv-owner-actions">
      <p className="text-xs text-amber-800 mb-2">
        This invoice is awaiting payment. Mark it as paid, or dispute it if something is wrong.
      </p>
      <div className="flex gap-2 flex-wrap items-center">
        <PaymentConfirmationUpload entry={entry} onUpdated={onUpdated} />
        <Button size="sm" onClick={() => setStatus("paid")} disabled={busy} className="bg-ink hover:bg-black" data-testid="inv-owner-payment-done">
          Payment done
        </Button>
        <Button size="sm" variant="outline" onClick={() => setDisputeOpen(true)} disabled={busy}
          className="border-red-300 text-red-700 hover:bg-red-50" data-testid="inv-owner-dispute">
          Dispute
        </Button>
      </div>

      <Dialog open={disputeOpen} onOpenChange={(o) => { if (!o) setDisputeOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid="inv-dispute-dialog">
          <DialogHeader>
            <DialogTitle>Dispute this invoice</DialogTitle>
            <DialogDescription>
              Tell the supplier what's wrong. Your reason is sent to them so they can correct the invoice and send it again.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. The second line item was not part of the agreed scope…"
            data-testid="inv-dispute-reason"
          />
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDisputeOpen(false)} data-testid="inv-dispute-cancel">Cancel</Button>
            <Button
              onClick={() => setStatus("dispute", { reason: reason.trim() })}
              disabled={busy || !reason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="inv-dispute-confirm"
            >
              Dispute invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Quotation documents from the linked task, shown on the invoice detail so the
 * paperwork sits next to the bill. */
function InvoiceTaskDocs({ taskId }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    let ok = true;
    api.get(`/maintenance/${taskId}`)
      .then(({ data }) => { if (ok) setDocs((data.documents || []).filter((d) => (d.kind || "") === "quotation")); })
      .catch(() => { if (ok) setDocs([]); });
    return () => { ok = false; };
  }, [taskId]);
  if (!docs || !docs.length) return null;
  return (
    <div className="mt-3 border-t border-zinc-200 pt-3" data-testid="inv-task-docs">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Documents</div>
      <ul className="space-y-1">
        {docs.map((d) => (
          <li key={d.id} className="flex items-center gap-2 min-w-0 text-sm">
            <FileText className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
            <AuthFileLink path={d.file_path} className="text-sky-700 hover:underline truncate min-w-0" testid={`inv-task-doc-${d.id}`}>{d.name || "Quotation"}</AuthFileLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Field2({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="mt-0.5 text-zinc-900">{children}</div>
    </div>
  );
}

/* Proof-of-payment (bank slip etc.) — attach button turning into a link chip. */
function PaymentConfirmationUpload({ entry, onUpdated }) {
  const attach = async (path) => {
    try {
      const { data } = await api.post(`/invoices/${entry.id}/payment-confirmation`, { file_path: path });
      toast.success("Payment confirmation attached");
      onUpdated?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  if (entry.payment_confirmation_path) {
    return (
      <a
        href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${entry.payment_confirmation_path}`}
        target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1 text-[11px] text-emerald-700 border border-emerald-200 bg-emerald-50 rounded px-2 py-1 hover:bg-emerald-100"
        data-testid="inv-payment-confirmation-link"
      >
        <FileText className="h-3 w-3" /> Payment confirmation
      </a>
    );
  }
  return (
    <FileUploader
      yacht_id={entry.yacht_id}
      folder="invoices"
      accept="application/pdf,image/*"
      label="Attach payment confirmation"
      testid="inv-attach-payment-confirmation"
      onUploaded={attach}
    />
  );
}

/* Offline-tracked invoice: the yacht crew manages it entirely on the
 * supplier's behalf — supplier-style editing + the full lifecycle
 * (Mark as sent → Payment done → Confirm payment received). No supplier
 * notifications, no messages. */
function OfflineInvoiceActions({ entry, onUpdated, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const editable = !["paid", "payment_confirmed"].includes(entry.status);

  const supStatus = async (status, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${entry.id}/supplier-status`, { status });
      toast.success(okMsg);
      onUpdated?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const markPaid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${entry.id}/status`, { status: "paid" });
      toast.success("Marked as Payment done");
      onUpdated?.(data);
    } catch (e) {
      if (!limitToast(e, { yacht_id: entry.yacht_id, kind: "invoice", ref_id: entry.id, label: entry.supplier_name || entry.invoice_number || "", amount: entry.amount })) {
        toast.error(fmtErr(e));
      }
    }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-3 space-y-3" data-testid="inv-offline-actions">
      <p className="text-xs text-zinc-600">
        Tracked offline — your crew manages this invoice on the supplier's behalf. No notifications are sent.
      </p>
      {editable && <OfflineInvoiceEditor entry={entry} onUpdated={onUpdated} />}
      <div className="flex gap-2 flex-wrap items-center">
        {entry.status === "task_confirmed" && (
          <Button size="sm" onClick={() => supStatus("waiting", "Invoice marked as final — now awaiting payment")} disabled={busy} className="bg-ink hover:bg-black" data-testid="inv-offline-mark-final">
            Mark as final
          </Button>
        )}
        {entry.status === "waiting" && (
          <>
            <PaymentConfirmationUpload entry={entry} onUpdated={onUpdated} />
            <Button size="sm" onClick={markPaid} disabled={busy} className="bg-ink hover:bg-black" data-testid="inv-offline-payment-done">
              Payment done
            </Button>
          </>
        )}
        {entry.status === "paid" && (
          <>
            <PaymentConfirmationUpload entry={entry} onUpdated={onUpdated} />
            <Button size="sm" onClick={() => supStatus("payment_confirmed", "Payment confirmed")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="inv-offline-confirm-payment">
              Confirm payment received
            </Button>
          </>
        )}
        <Button
          size="sm" variant="outline" disabled={busy}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          onClick={async () => {
            if (!window.confirm("Permanently delete this offline invoice? The task itself stays. This cannot be undone.")) return;
            setBusy(true);
            try {
              const { data } = await api.delete(`/invoices/${entry.id}`);
              if (data?.mode === "approval_requested") { toast.success("Deletion request sent to the owner/manager for approval"); onUpdated?.(); }
              else if (data?.mode === "approval_pending") toast.info("A deletion request is already awaiting approval");
              else { toast.success("Invoice deleted"); onDeleted?.(); }
            } catch (e) { toast.error(fmtErr(e)); }
            finally { setBusy(false); }
          }}
          data-testid="inv-offline-delete"
        >
          <Trash2 className="h-4 w-4 mr-1" />Delete invoice
        </Button>
      </div>
      {entry.status === "payment_confirmed" && (
        <InvoiceReviewBlock invoiceId={entry.id} supplierName={entry.supplier_name} canReview />
      )}
    </div>
  );
}

/* Supplier-style inline editor, driven by the yacht: scope line items with
 * prices (total recalculates), invoice #, due date, bank details, notes,
 * attach PDF. Saves via the supplier-edit endpoint. */
function OfflineInvoiceEditor({ entry, onUpdated }) {
  const [form, setForm] = useState(null); // null → collapsed
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const start = () => {
    setForm({
      invoice_number: entry.invoice_number || "",
      issue_date: (entry.issue_date || "").slice(0, 10),
      due_date: (entry.due_date || "").slice(0, 10),
      vat: entry.vat_percent != null ? String(entry.vat_percent) : "",
      notes: entry.notes || "",
      bank_name: entry.bank_name || "",
      account_holder: entry.account_holder || "",
      iban: entry.iban || "",
      bic_swift: entry.bic_swift || "",
      file_path: entry.file_path || null,
    });
    setItems((entry.line_items || []).length
      ? entry.line_items.map((li) => ({ text: li.text || "", price: li.price ?? "" }))
      : [{ text: "", price: "" }]);
  };

  const priced = items.filter((i) => i.price !== "" && i.price != null);
  const subtotal = priced.reduce((s, i) => s + Number(i.price || 0), 0);
  const vatPct = form?.vat === "" || form?.vat == null ? null : Number(form.vat);
  const vatAmt = vatPct ? Math.round(subtotal * vatPct) / 100 : 0;
  const total = Math.round((subtotal + vatAmt) * 100) / 100;
  const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const setItem = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoices/${entry.id}/supplier-edit`, {
        amount: priced.length ? total : null,
        vat_percent: vatPct,
        invoice_number: form.invoice_number || null,
        issue_date: form.issue_date || null,
        due_date: form.due_date || null,
        notes: form.notes || null,
        bank_name: form.bank_name || null,
        account_holder: form.account_holder || null,
        iban: form.iban || null,
        bic_swift: form.bic_swift || null,
        file_path: form.file_path || null,
        line_items: items
          .filter((i) => (i.text || "").trim())
          .map((i) => ({ text: i.text.trim(), price: i.price === "" || i.price == null ? null : Number(i.price) })),
      });
      toast.success("Invoice updated");
      setForm(null);
      onUpdated?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSaving(false); }
  };

  if (!form) {
    return (
      <Button size="sm" variant="outline" onClick={start} data-testid="inv-offline-edit">
        <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit invoice
      </Button>
    );
  }

  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3 space-y-3" data-testid="inv-offline-editor">
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Invoice items</div>
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="text-xs text-zinc-400 w-4 shrink-0 pt-2">{i + 1}.</span>
            <Textarea value={it.text} onChange={(e) => setItem(i, { text: e.target.value })} rows={3} placeholder="Item description" className="flex-1 text-sm min-h-0" data-testid={`inv-offline-item-text-${i}`} />
            <Input type="number" step="0.01" min="0" value={it.price} onChange={(e) => setItem(i, { price: e.target.value })} placeholder="Price" className="w-20 h-8 text-sm no-spinner shrink-0" data-testid={`inv-offline-item-price-${i}`} />
            <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-red-600 shrink-0 pt-2" data-testid={`inv-offline-item-remove-${i}`} title="Remove item">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <div className="flex items-start gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { text: "", price: "" }])} data-testid="inv-offline-item-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add item
          </Button>
          <div className="flex items-center gap-1 ml-auto">
            <Input type="number" min="0" max="100" placeholder="VAT" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} className="w-16 h-8 text-sm no-spinner" data-testid="inv-offline-vat" />
            <span className="text-xs text-zinc-500">VAT&nbsp;%</span>
          </div>
        </div>
        {priced.length > 0 && (
          <div className="border-t border-zinc-200 pt-2 space-y-0.5 text-xs" data-testid="inv-offline-total">
            {vatPct != null && (
              <>
                <div className="flex justify-between text-zinc-500"><span>Subtotal</span><span className="font-mono">{entry.currency} {money(subtotal)}</span></div>
                <div className="flex justify-between text-zinc-500"><span>VAT {vatPct}%</span><span className="font-mono">{entry.currency} {money(vatAmt)}</span></div>
              </>
            )}
            <div className="flex justify-between text-sm font-semibold">
              <span>Total{vatPct != null ? " incl. VAT" : ""} (recalculated)</span>
              <span className="font-mono">{entry.currency} {money(total)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Invoice #</div>
          <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="h-8 text-sm" data-testid="inv-offline-number" />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Issue date</div>
          <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="h-8 text-sm" data-testid="inv-offline-issue" />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Due date</div>
          <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-8 text-sm" data-testid="inv-offline-due" />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Pay to (bank details)</div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Bank name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="h-8 text-sm" data-testid="inv-offline-bank-name" />
          <Input placeholder="Account holder" value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} className="h-8 text-sm" data-testid="inv-offline-account-holder" />
          <Input placeholder="IBAN" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="h-8 text-sm" data-testid="inv-offline-iban" />
          <Input placeholder="BIC / SWIFT" value={form.bic_swift} onChange={(e) => setForm({ ...form, bic_swift: e.target.value })} className="h-8 text-sm" data-testid="inv-offline-bic" />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Notes</div>
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="inv-offline-notes" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <FileUploader
          yacht_id={entry.yacht_id}
          folder="invoices"
          accept="application/pdf,image/*"
          label={form.file_path ? "Replace PDF" : "Attach invoice PDF"}
          testid="inv-offline-upload"
          onUploaded={(path) => { setForm((f) => ({ ...f, file_path: path })); toast.success("File attached — remember to Save"); }}
        />
      </div>

      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={saving} className="bg-ink hover:bg-black" data-testid="inv-offline-save">
          {saving ? "Saving…" : "Save invoice"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setForm(null)} disabled={saving} data-testid="inv-offline-cancel">
          Cancel
        </Button>
      </div>
    </div>
  );
}
